import { useCallback } from "react";
import { AccountInfo, InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useAuthConfig, useAuthStatus } from "./context";
import { resetAutoLoginGuard, startAutoRedirect } from "./loginGuard";

export interface AuthUser {
  email?: string;
  name?: string;
}

function claim(account: AccountInfo, key: string): string | undefined {
  const value = account.idTokenClaims?.[key];
  return typeof value === "string" ? value : undefined;
}

/** Display-only user data from the ID-token claims; never used for authorization. */
function toUser(account: AccountInfo): AuthUser {
  return { email: claim(account, "email"), name: claim(account, "name") ?? account.name };
}

function currentAccount(instance: IPublicClientApplication): AccountInfo | null {
  return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
}

const LOGIN_SCOPES = ["openid", "profile", "offline_access"];

export function useAuth() {
  const { instance, accounts } = useMsal();
  const { apiScopes, domainHint, postLogoutRedirectUri } = useAuthConfig();
  const { reportSessionEnded } = useAuthStatus();
  const isAuthenticated = useIsAuthenticated();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  const user = isAuthenticated && account ? toUser(account) : undefined;

  // Stable identity: pages list it in useEffect dependencies.
  const getAccessTokenSilently = useCallback(async (options: { forceRefresh?: boolean } = {}): Promise<string> => {
    const request = {
      scopes: apiScopes,
      account: currentAccount(instance) ?? undefined,
      ...(options.forceRefresh ? { forceRefresh: true } : {}),
    };
    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Guarded like every automatic redirect; navigates away when started.
        const redirect = startAutoRedirect(instance, () => instance.acquireTokenRedirect(request));
        if (redirect.outcome === "blocked") reportSessionEnded();
        await redirect.done.catch(() => reportSessionEnded());
      }
      throw error;
    }
  }, [instance, apiScopes, reportSessionEnded]);

  const loginWithRedirect = useCallback(async (): Promise<void> => {
    const scopes = [...LOGIN_SCOPES, ...apiScopes];
    await instance.loginRedirect(domainHint ? { scopes, domainHint } : { scopes });
  }, [instance, apiScopes, domainHint]);

  // logoutRedirect also removes the account's tokens from the MSAL cache (sessionStorage).
  const logout = useCallback(async (): Promise<void> => {
    resetAutoLoginGuard(instance);
    await instance.logoutRedirect({ account: currentAccount(instance) ?? undefined, postLogoutRedirectUri });
  }, [instance, postLogoutRedirectUri]);

  /** Automatic sign-in (e.g. API keeps answering 401): guarded, single-flight. */
  const startAutoLogin = useCallback(async (): Promise<void> => {
    const redirect = startAutoRedirect(instance, loginWithRedirect);
    if (redirect.outcome === "blocked") reportSessionEnded();
    // MSAL refused to start the redirect: show the session-ended page ("Sign in again").
    await redirect.done.catch(() => reportSessionEnded());
  }, [instance, loginWithRedirect, reportSessionEnded]);

  return { isAuthenticated, user, getAccessTokenSilently, loginWithRedirect, startAutoLogin, logout };
}
